import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import api from '@/lib/api';
import type { User } from '@/types';

interface MentionInputProps {
  value: string;
  onChange: (value: string) => void;
  onMentionsChange: (mentionedUserIds: string[]) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  disabled?: boolean;
  placeholder?: string;
}

// Role group definitions
const ROLE_GROUPS = [
  { id: 'role:caretakers', label: '@caretakers', description: 'All caretakers' },
  { id: 'role:guardians', label: '@guardians', description: 'All guardians' },
  { id: 'role:family', label: '@family', description: 'All family members' },
  { id: 'role:all', label: '@all', description: 'Everyone' },
];

// Role-based fallback colors
const ROLE_COLORS: Record<string, string> = {
  family: '#F59E0B',
  professional: '#14B8A6',
};

function getUserColor(user?: { color?: string | null; role?: string } | null): string {
  if (user?.color) return user.color;
  if (user?.role && ROLE_COLORS[user.role]) return ROLE_COLORS[user.role];
  return '#6b7280';
}

export function MentionInput({ value, onChange, onMentionsChange, onKeyDown, disabled, placeholder }: MentionInputProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch users for mention dropdown
  const { data: users = [] } = useQuery<User[]>({
    queryKey: ['users-for-mentions'],
    queryFn: async () => {
      try {
        const response = await api.get<User[]>('/users');
        const data = response.data;
        return Array.isArray(data) ? data : (data as any).users || [];
      } catch {
        return [];
      }
    },
    staleTime: 1000 * 60,
  });

  // Filter role groups based on mention query
  const filteredRoleGroups = useMemo(() => {
    if (!mentionQuery) return ROLE_GROUPS;
    return ROLE_GROUPS.filter((g) =>
      g.label.substring(1).toLowerCase().startsWith(mentionQuery.toLowerCase())
    );
  }, [mentionQuery]);

  // Filter users based on mention query
  const filteredUsers = mentionQuery
    ? users.filter((u) =>
        u.first_name.toLowerCase().startsWith(mentionQuery.toLowerCase()) ||
        (u.last_name && u.last_name.toLowerCase().startsWith(mentionQuery.toLowerCase()))
      )
    : users;

  // Combined items: role groups first, then users
  const allItems = useMemo(() => {
    const items: Array<{ type: 'group'; group: typeof ROLE_GROUPS[0] } | { type: 'user'; user: User }> = [];
    filteredRoleGroups.forEach((g) => items.push({ type: 'group', group: g }));
    filteredUsers.slice(0, 8 - filteredRoleGroups.length).forEach((u) => items.push({ type: 'user', user: u }));
    return items;
  }, [filteredRoleGroups, filteredUsers]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    onChange(newValue);

    // Check if we're in a mention context
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex >= 0) {
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      if (charBeforeAt === ' ' || lastAtIndex === 0) {
        const query = textBeforeCursor.substring(lastAtIndex + 1);
        if (!query.includes(' ')) {
          setShowDropdown(true);
          setMentionQuery(query);
          setMentionStart(lastAtIndex);
          setSelectedIndex(0);
          return;
        }
      }
    }

    setShowDropdown(false);
    setMentionQuery('');
    setMentionStart(-1);
  };

  const selectUser = useCallback((user: User) => {
    const before = value.substring(0, mentionStart);
    const after = value.substring(mentionStart + 1 + mentionQuery.length);
    const newValue = `${before}@${user.first_name} ${after}`;
    onChange(newValue);

    // Track mentioned user
    if (!mentionedUserIds.includes(user.id)) {
      const newIds = [...mentionedUserIds, user.id];
      setMentionedUserIds(newIds);
      onMentionsChange(newIds);
    }

    setShowDropdown(false);
    setMentionQuery('');
    setMentionStart(-1);

    // Refocus input
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [value, mentionStart, mentionQuery, mentionedUserIds, onChange, onMentionsChange]);

  const selectRoleGroup = useCallback((group: typeof ROLE_GROUPS[0]) => {
    const before = value.substring(0, mentionStart);
    const after = value.substring(mentionStart + 1 + mentionQuery.length);
    const newValue = `${before}${group.label} ${after}`;
    onChange(newValue);

    // Track role group mention
    if (!mentionedUserIds.includes(group.id)) {
      const newIds = [...mentionedUserIds, group.id];
      setMentionedUserIds(newIds);
      onMentionsChange(newIds);
    }

    setShowDropdown(false);
    setMentionQuery('');
    setMentionStart(-1);

    setTimeout(() => inputRef.current?.focus(), 0);
  }, [value, mentionStart, mentionQuery, mentionedUserIds, onChange, onMentionsChange]);

  const selectItem = useCallback((index: number) => {
    const item = allItems[index];
    if (!item) return;
    if (item.type === 'group') {
      selectRoleGroup(item.group);
    } else {
      selectUser(item.user);
    }
  }, [allItems, selectRoleGroup, selectUser]);

  const handleKeyDownInternal = (e: React.KeyboardEvent) => {
    if (showDropdown && allItems.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, allItems.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectItem(selectedIndex);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowDropdown(false);
        return;
      }
    }
    onKeyDown(e);
  };

  // Reset mentions when value is cleared (after send)
  useEffect(() => {
    if (value === '') {
      setMentionedUserIds([]);
      onMentionsChange([]);
    }
  }, [value, onMentionsChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative flex-1">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDownInternal}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        disabled={disabled}
        data-testid="input-chat-message"
      />

      {/* Mention dropdown */}
      {showDropdown && allItems.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute bottom-full mb-1 left-0 right-0 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-48 overflow-y-auto z-50"
          data-testid="container-mention-dropdown"
        >
          {allItems.map((item, idx) => {
            if (item.type === 'group') {
              return (
                <button
                  key={item.group.id}
                  onClick={() => selectRoleGroup(item.group)}
                  data-testid={`clickable-mention-group-${item.group.id}`}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-700 transition-colors ${
                    idx === selectedIndex ? 'bg-slate-700' : ''
                  }`}
                >
                  <span className="w-6 h-6 rounded-full bg-purple-500/30 flex items-center justify-center">
                    <Users size={14} className="text-purple-300" />
                  </span>
                  <span className="text-white text-sm font-medium">{item.group.label}</span>
                  <span className="text-gray-400 text-xs ml-auto">{item.group.description}</span>
                </button>
              );
            }
            const user = item.user;
            return (
              <button
                key={user.id}
                onClick={() => selectUser(user)}
                data-testid={`clickable-mention-user-${user.id}`}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-700 transition-colors ${
                  idx === selectedIndex ? 'bg-slate-700' : ''
                }`}
              >
                {user.emoji ? (
                  <span className="text-lg">{user.emoji}</span>
                ) : (
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                    style={{ backgroundColor: getUserColor(user) }}
                  >
                    {user.first_name.charAt(0)}
                  </span>
                )}
                <span className="text-white text-sm">{user.first_name}</span>
                {user.last_name && (
                  <span className="text-gray-400 text-sm">{user.last_name}</span>
                )}
                <span
                  className="text-xs px-1.5 py-0.5 rounded ml-auto"
                  style={{ color: getUserColor(user), backgroundColor: `${getUserColor(user)}20` }}
                >
                  {user.role === 'parent' ? 'Guardian' : user.role}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
